import { AnswerHeadline } from "@nimit9/signet-ui/components/answer-headline"
import { Button } from "@nimit9/signet-ui/components/button"
import { ErrorState } from "@nimit9/signet-ui/components/error-state"
import { RatioFill, RatioText } from "@nimit9/signet-ui/components/privacy-mask"
import { Skeleton } from "@nimit9/signet-ui/components/skeleton"
import { StatStrip } from "@nimit9/signet-ui/components/stat-strip"
import { useMediaQuery } from "@nimit9/signet-ui/hooks/use-media-query"
import { createFileRoute } from "@tanstack/react-router"
import { PrinterIcon } from "lucide-react"
import { useMemo } from "react"
import { Amount } from "@/components/common/amount"
import { Header } from "@/components/layout/header"
import { PageWrapper } from "@/components/layout/page-wrapper"
import { type MonthlyReport, useReports } from "@/hooks/use-reports"
import { renderAnswerAmount, toSignetHeadline } from "@/lib/answer-headline"
import { cn } from "@/lib/cn"
import { CATEGORY_COLORS } from "@/lib/constants"
import { buildReportsHeadline, savingsTone } from "./-headline"

export const Route = createFileRoute("/reports/")({
  component: Reports,
})

const RATE_CHIP = {
  good: "bg-accent-green-subtle text-accent-green",
  warn: "bg-accent-amber-subtle text-accent-amber",
  neutral: "bg-bg-elevated text-text-secondary",
} as const

function MetricRow({
  label,
  children,
  sub,
  tone,
}: {
  label: string
  children: React.ReactNode
  sub?: string
  tone?: "good" | "warn"
}) {
  return (
    <div className="flex items-center justify-between gap-3 py-1.5">
      <span className="text-body text-text-secondary">{label}</span>
      <span
        className={cn(
          "text-body font-medium text-text-primary",
          tone === "good" && "text-accent-green",
          tone === "warn" && "text-accent-amber",
        )}
      >
        {children}
        {sub && <span className="ml-1 text-caption font-normal text-text-muted">{sub}</span>}
      </span>
    </div>
  )
}

function ReportCard({ report }: { report: MonthlyReport }) {
  const tone = savingsTone(report.savingsRate)
  return (
    <article
      data-testid="report-card"
      className="flex min-w-0 flex-col gap-3 rounded-xl bg-bg-surface p-4 ring-1 ring-border-subtle md:p-5 print:break-inside-avoid"
    >
      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-callout font-semibold text-text-primary">{report.label}</h2>
          {!report.hasData && (
            <p className="mt-0.5 text-caption text-text-muted">No data for this month</p>
          )}
        </div>
        {report.netWorth != null && (
          <div className="shrink-0 text-right">
            <p className="text-caption text-text-muted">Net worth</p>
            <Amount value={report.netWorth} className="text-body font-medium" />
          </div>
        )}
      </header>

      {report.hasData && (
        <>
          <div className="rounded-lg bg-bg-elevated px-3 py-1.5">
            <MetricRow label="Income">
              <Amount value={report.income} />
            </MetricRow>
            <MetricRow label="Expenses">
              <Amount value={report.expenses} />
            </MetricRow>
            <MetricRow label="Invested">
              <Amount value={report.invested} />
            </MetricRow>
            <div className="my-1 border-t border-border-subtle" />
            <MetricRow
              label="Surplus"
              sub={report.surplus < 0 ? "(deficit)" : undefined}
              tone={report.surplus < 0 ? "warn" : "good"}
            >
              <Amount value={report.surplus} className="text-current" />
            </MetricRow>
          </div>

          <div className="flex flex-col gap-2">
            {[
              { key: "needs", label: "Needs", value: report.needs },
              { key: "wants", label: "Wants", value: report.wants },
            ].map((item) => {
              const pct = report.expenses > 0 ? (item.value / report.expenses) * 100 : 0
              return (
                <div key={item.key}>
                  <div className="flex items-center justify-between gap-2 text-caption">
                    <span className="text-text-muted">{item.label}</span>
                    <span className="text-text-secondary">
                      <Amount value={item.value} />
                      {report.expenses > 0 && (
                        <span className="ml-1 text-text-muted">
                          (<RatioText>{`${Math.round(pct)}%`}</RatioText>)
                        </span>
                      )}
                    </span>
                  </div>
                  <div aria-hidden className="mt-1 h-1 rounded-full bg-bg-elevated">
                    <RatioFill
                      pct={Math.min(pct, 100)}
                      className="h-full rounded-full"
                      style={{ backgroundColor: CATEGORY_COLORS[item.key] }}
                    />
                  </div>
                </div>
              )
            })}
          </div>

          <div className="flex items-center justify-between">
            <span className="text-caption text-text-muted">Savings rate</span>
            <span
              className={cn(
                "rounded-full px-2.5 py-0.5 font-mono text-caption font-medium",
                RATE_CHIP[tone],
              )}
            >
              <RatioText>{`${report.savingsRate}%`}</RatioText>
            </span>
          </div>
        </>
      )}
    </article>
  )
}

function Reports() {
  const isDesktop = useMediaQuery("(min-width: 768px)")
  const { data, isLoading, isError, error, refetch } = useReports(6)
  const reports = useMemo(() => data?.data ?? [], [data])
  const headline = useMemo(() => buildReportsHeadline(reports), [reports])

  const printAction = (
    <Button size="sm" variant="ghost" aria-label="Save as PDF" onClick={() => window.print()}>
      <PrinterIcon />
      <span className="hidden sm:inline">Save as PDF</span>
      <span className="sm:hidden">PDF</span>
    </Button>
  )

  if (isLoading) {
    return (
      <>
        <Header title="Reports" showPeriod={false} />
        <PageWrapper>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Skeleton key={i} className="h-72 animate-pulse ring-1 ring-border-subtle" />
            ))}
          </div>
        </PageWrapper>
      </>
    )
  }

  if (isError) {
    return (
      <>
        <Header title="Reports" showPeriod={false} />
        <PageWrapper>
          <ErrorState message={(error as Error)?.message} onRetry={refetch} />
        </PageWrapper>
      </>
    )
  }

  const withData = reports.filter((r) => r.hasData)
  const avgSavingsRate =
    withData.length > 0
      ? Math.round(withData.reduce((s, r) => s + r.savingsRate, 0) / withData.length)
      : 0
  const totalInvested = reports.reduce((s, r) => s + r.invested, 0)
  const totalExpenses = reports.reduce((s, r) => s + r.expenses, 0)
  const avgTone = savingsTone(avgSavingsRate)

  return (
    <>
      <Header title="Reports" showPeriod={false} action={printAction} />
      <PageWrapper>
        <div
          className="flex min-w-0 flex-col gap-4 md:gap-5"
          data-testid="reports-page"
          data-layout={isDesktop ? "desktop" : "phone"}
        >
          {headline && (
            <AnswerHeadline
              headline={toSignetHeadline(headline)}
              renderValue={renderAnswerAmount}
            />
          )}

          {withData.length > 0 && (
            <StatStrip
              isDesktop={isDesktop}
              items={[
                {
                  label: `Avg savings rate (${withData.length}mo)`,
                  value: `${avgSavingsRate}%`,
                  meta:
                    avgTone === "good" ? "On track" : avgTone === "warn" ? "Below 15%" : undefined,
                  tone: avgTone === "neutral" ? "muted" : avgTone,
                },
                { label: "Total invested", value: <Amount value={totalInvested} /> },
                { label: "Total expenses", value: <Amount value={totalExpenses} /> },
              ]}
            />
          )}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {reports.map((report) => (
              <ReportCard key={report.key} report={report} />
            ))}
          </div>
        </div>
      </PageWrapper>
    </>
  )
}
